import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from './auth';
import { isDoctor, isAdmin, isGestion, isManagement, isTablet, isTabletConsent } from './roles';
import { ClinicPilotMark } from '../components/ClinicPilotBrand';

export function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3">
        <ClinicPilotMark size={44} className="animate-pulse" />
        <div className="text-sm text-muted-foreground">Cargando…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Las tablets, a su kiosco (segunda pasada de M33, 23/9/2026). Dentro del
  // programa del equipo no hay nada suyo: el servidor les contesta 403 a todo
  // (`get_current_staff_user`). Y lo grave: una tablet con el cambio de
  // contraseña pendiente que abría «/» se encontraba el diálogo de «Cambia tu
  // contraseña», que no se puede cerrar y que el servidor ya siempre rechaza
  // (M33): el iPad se quedaba atrapado. El login ya las mandaba a su kiosco;
  // faltaba cuando alguien escribe la dirección o recarga en otra ruta.
  // Las rutas del kiosco están fuera de RequireAuth, así que no hay bucle.
  if (isTablet(user)) return <Navigate to="/firma-lopd" replace />;
  if (isTabletConsent(user)) return <Navigate to="/firma-consent" replace />;
  return children;
}

/**
 * Los doctores solo acceden a lo puramente clínico: agenda, recetas,
 * informes y la ficha del paciente (donde escriben los evolutivos). El
 * resto de rutas les redirige a su agenda — también si entran por URL.
 */
export function NoDoctores({ children }) {
  const { user } = useAuth();
  if (isDoctor(user)) return <Navigate to="/citas" replace />;
  return children;
}

/**
 * Solo ADMINISTRADORES. Los honorarios
 * y datos económicos sensibles NO son para administrativos ni enfermería.
 * Quien no sea admin (o entre por URL) se redirige al dashboard. El backend
 * también lo comprueba con require_role("admin").
 */
/**
 * ClinicPilot Drive: administradores, dirección y administrativos.
 * (Doctores y enfermería fuera; el backend lo exige igualmente.)
 */
export function SoloGestion({ children }) {
  const { user } = useAuth();
  // Con el helper de roles, como el menú (M17, 23/9/2026). Comparando el rol
  // crudo, a recepción, médico y secretaría (los roles viejos) el menú les
  // enseñaba Leads y Devoluciones y esta ruta los echaba al inicio, cuando el
  // servidor (`require_gestion`) sí los deja entrar. La regla la pone el
  // servidor: aquí solo se evita enseñar una puerta y cerrarla al pulsar.
  // Ojo: Mail y WhatsApp también pasan por aquí y su servidor NO admite los
  // roles viejos (`require_role` de los tres nuevos). Los roles viejos no
  // duran (seed.run_seed los pasa a los nuevos en cada arranque, y Usuarios
  // no los ofrece); cuál de las dos reglas vale para Mail y WhatsApp queda
  // para dirección.
  if (!isGestion(user)) return <Navigate to="/" replace />;
  return children;
}

/** Administración y dirección (admin + dirección): lo económico de la
 *  sociedad entera, como Facturación. El servidor lo exige igual. */
export function SoloDireccion({ children }) {
  const { user } = useAuth();
  if (!isManagement(user)) return <Navigate to="/" replace />;
  return children;
}

export function SoloAdmin({ children }) {
  const { user } = useAuth();
  if (!isAdmin(user)) return <Navigate to="/" replace />;
  return children;
}
